import { Injector } from "@angular/core";
import { ClaimsTypes } from "@bbwt/modules/security/claims-types";
import { SetUTCTimeZone } from "@bbwt/utils";
import { Permissions } from "@main/permissions/permissions";
import { CoreRole } from "@main/roles";
import { UserHelperService } from "@main/users/user-helper.service";
import { MiProModules } from "@mipro/classes/mipro-modules";

export interface CacheDataGroup {
    name: string;
    urls?: UrlAccess[];
    templUrls?: UrlAccess[];
    buildUrls?: (tempUrl: string, injector?: Injector) => string[];
    header: string;
    access?: Access;
}

/**
 * The access is computed in the following way (backend-wise):
 * (userHas(roles+Anyone) || userHas(permission)) && (empty(miproModules) || organizationHas(miproModules))
 *
 * If restricting only by MiPro Modules add Anyone to the access roles so the
 * above condition is met.
 *
 * If no access configured it means everyone can access.
 */
export interface Access {
    roles?: string[];
    permissions?: string[];
    miproModules?: string[];
}

export type UrlAccess = string | [string, Access];

export const AnyoneRole = "Anyone";

export const cacheDataGroups: CacheDataGroup[] = [
    {
        name: "account_login_home_cached_routes",
        header: "Login, Account & Site information",
        urls: [
            "/api/account/ip-locking-time",
            "/api/footer-menu",
            "/api/linked-user",
            "/api/menu/me",
            "/api/role/model",
            "/api/route/me",
            "/api/system-configuration",
            "/api/system-configuration/pwa-enabled",
            "/api/system-data/version",
            "/api/user/claims",
            "/api/user/get-avatar-image",
            "/api/user/me",
            "/api/user/me-name-conv",
            "/api/formio-inbox/total",
            "/api/formio-module/existing-module-forms",
            "/api/account/linked-orgs",
            "/api/home/cards/modules/default/published-dashboards",
            "/api/home/cards/is-diary-module-lhs-card-visible",
            "/api/home/cards/modules/formio/published-dashboards",
            "/api/im1/is-registered",
            "/api/im1/limited-access"
        ]
    },
    {
        name: "users_cache",
        header: "User Information",
        urls: [
            [
                "/api/company/all-plain",
                {
                    roles: [CoreRole.SuperAdmin, CoreRole.MiProAdmin],
                    permissions: [Permissions.CreateUser, Permissions.ArchiveUser]
                }
            ],
            [
                "/api/end-user/organization-groups",
                {
                    roles: [
                        CoreRole.SuperAdmin,
                        CoreRole.MiProAdmin,
                        CoreRole.GovBodyAdmin,
                        CoreRole.OrganisationAdmin,
                        CoreRole.NonClinicalAdmin,
                        CoreRole.ClinicalAdmin,
                        CoreRole.Doctor,
                        CoreRole.Therapist,
                        CoreRole.StrengthAndConditioning,
                        CoreRole.Nutritionist
                    ]
                }
            ],
            "api/role/current-organization",
            "api/company/allow-clinicians-modify-clinics",
            "api/company/current-organization-appointment-booking-url",
            "api/company/clinician-usertypes",
            "/api/end-user/squads"
        ],
        templUrls: [
            [
                "/api/company/page?skip=@skip&take=@take&sortingDirection=1&sortingField=",
                { roles: [CoreRole.SuperAdmin, CoreRole.MiProAdmin] }
            ],
            [
                "/api/end-user/page?skip=@skip&take=@take&sortingDirection=1&sortingField=firstName",
                { permissions: [Permissions.ReadPatient, Permissions.ArchivePatient] }
            ],
            [
                "/api/end-user/organization-groups/@organizationId",
                {
                    roles: [
                        CoreRole.SuperAdmin,
                        CoreRole.MiProAdmin,
                        CoreRole.GovBodyAdmin,
                        CoreRole.OrganisationAdmin,
                        CoreRole.NonClinicalAdmin,
                        CoreRole.ClinicalAdmin,
                        CoreRole.Doctor,
                        CoreRole.Therapist,
                        CoreRole.StrengthAndConditioning,
                        CoreRole.Nutritionist
                    ]
                }
            ]
        ],
        buildUrls(templUrl: string, injector: Injector) {
            const urls: string[] = [];
            if (/^\/api\/[^\/]+\/page/.exec(templUrl)) {
                for (const pageSize of [5, 10, 20]) {
                    for (const pageNumber of [0, 1, 2]) {
                        urls.push(templUrl.replace("@skip", (pageNumber * pageSize).toString()).replace("@take", pageSize.toString()));
                    }
                }
                return urls;
            }

            const userHelperService = injector?.get(UserHelperService);
            const orgIds = userHelperService?.findFirstValue(ClaimsTypes.AllTenantIds)?.split(",") ?? [];
            for (const orgId of orgIds) {
                urls.push(templUrl.replace("@organizationId", orgId));
            }

            return urls;
        }
    },
    {
        name: "patients_cache",
        header: "Patients Information",
        urls: ["/api/patients/all"],
        access: {
            roles: [
                CoreRole.MiProAdmin,
                CoreRole.SuperAdmin,
                CoreRole.SystemTester,
                CoreRole.GovBodyAdmin,
                CoreRole.OrganisationAdmin,
                CoreRole.NonClinicalAdmin,
                CoreRole.ClinicalAdmin,
                CoreRole.Doctor,
                CoreRole.Therapist,
                CoreRole.StrengthAndConditioning,
                CoreRole.Nutritionist,
                CoreRole.GenericLogin,
                CoreRole.ClinicalSafetyOfficer
            ]
        }
    },
    {
        name: "im1_cache",
        header: "IM1 Information",
        urls: ["/api/im1/is-registered", "/api/im1/limited-access"]
    },
    {
        name: "diagnoses_cache",
        header: "Diagnosis Information",
        urls: [
            "/api/diagnosis/osiics/fields",
            "/api/diagnosis/osiics/body-parts",
            "/api/diagnosis/search?filters%5B0%5D.propertyName=searchTerm&filters%5B0%5D.value=&filters%5B0%5D.$type=string&filters%5B0%5D.matchMode=10"
        ],
        access: {
            roles: [AnyoneRole],
            miproModules: [MiProModules.Consultations, MiProModules.Diary, MiProModules.Prescriptions]
        }
    },
    {
        name: "medication_options_cache",
        header: "Medications Information",
        templUrls: ["/api/medications/options", "/api/medications/top/me"],
        buildUrls(tempUrl: string, injector: Injector) {
            const userHelperService = injector.get(UserHelperService);
            return !userHelperService || userHelperService.isPatient ? [] : [tempUrl];
        },
        access: {
            permissions: [Permissions.ReadPrescriptions, Permissions.WritePrescriptions],
            miproModules: [MiProModules.Prescriptions]
        }
    },
    {
        name: "walk-in_cache",
        header: "Clinicians Information",
        urls: ["/api/user/organization-clinicians"],
        access: {
            roles: [
                CoreRole.MiProAdmin,
                CoreRole.SuperAdmin,
                CoreRole.SystemTester,
                CoreRole.OrganisationAdmin,
                CoreRole.GovBodyAdmin,
                CoreRole.NonClinicalAdmin,
                CoreRole.ClinicalAdmin,
                CoreRole.Doctor,
                CoreRole.Therapist,
                CoreRole.StrengthAndConditioning,
                CoreRole.Nutritionist,
                CoreRole.GenericLogin,
                CoreRole.ClinicalSafetyOfficer
            ],
            miproModules: [MiProModules.Diary, MiProModules.Consultations]
        }
    },
    {
        name: "walk-in_performance_cache",
        header: "Treatments Information",
        urls: ["/api/walk-ins/get-treatments"],
        access: {
            permissions: [Permissions.WriteDiary],
            miproModules: [MiProModules.Diary]
        }
    },
    {
        name: "raven_cache_freshness",
        header: "Raven Information",
        urls: [
            "/api/notifications/unarchived",
            "/api/raven-api/check-statuses",
            "/api/raven-api/default-log",
            "/api/raven-api/getlogs",
            "/api/raven-api/is-config-valid",
            "/api/raven-api/priorities/all-priorities",
            "/api/raven-category/all-categories",
            "/api/raven-status/page"
        ],
        access: {
            roles: [AnyoneRole],
            miproModules: [MiProModules.Raven]
        }
    },
    {
        name: "appointments_offline_page_cache",
        header: "Appointments Offline Cache Information",
        templUrls: [
            "/api/clinic-slots/page-offline?" +
            "filters%5B0%5D.propertyName=startDate&filters%5B0%5D.value=@startDate&filters%5B0%5D.$type=date&filters%5B0%5D.matchMode=5" +
            "&filters%5B1%5D.propertyName=endDate&filters%5B1%5D.value=@endDate&filters%5B1%5D.$type=date&filters%5B1%5D.matchMode=3"
        ],
        buildUrls(tempUrl: string) {
            const urls: string[] = [];

            // Calculate the monday of the current week:
            const today = new Date();
            const dayOfWeek = today.getDay();
            const mondayOffset = (dayOfWeek + 6) % 7;
            const startOfCurrentWeek = new Date(today);
            startOfCurrentWeek.setDate(today.getDate() - mondayOffset);
            startOfCurrentWeek.setHours(0, 0, 0, 0);

            // Set url for the past week, current week and next week:
            for(let i = -1; i <= 1; i++){
                const startOfWeek = new Date(startOfCurrentWeek);
                startOfWeek.setDate(startOfWeek.getDate() + i * 7);

                const endOfWeek = new Date(startOfWeek);
                endOfWeek.setDate(startOfWeek.getDate() + 6);
                endOfWeek.setHours(23, 59, 59, 999);

                urls.push(tempUrl
                    .replace("@startDate", SetUTCTimeZone(startOfWeek).toUTCString())
                    .replace("@endDate", SetUTCTimeZone(endOfWeek).toUTCString()));
            }

            return urls;
        },
        access: {
            permissions: [Permissions.ReadDiary],
            miproModules: [MiProModules.Diary]
        }
    },
    {
        name: "appointments_cache",
        header: "Appointments Information",
        urls: [
            "/api/clinic-type/page",
            "/api/clinic-slots/filters/users",
            "/api/appointment-location/page",
            "/api/company/clinician-usertypes",
            "/api/company/current-organization-appointment-booking-url",
            "/api/appointment-favorite-filters"
        ],
        access: {
            permissions: [Permissions.ReadDiary],
            miproModules: [MiProModules.Diary]
        }
    },
    {
        name: "appointments_type_cache",
        header: "Appointments Type Information",
        templUrls: ["/api/appointment-type/from-organization/@organizationId"],
        buildUrls(templUrl: string, injector: Injector) {
            const urls: string[] = [];
            const userHelperService = injector?.get(UserHelperService);
            const orgId = userHelperService?.hashedOrganizationId;
            urls.push(templUrl.replace("@organizationId", orgId));
            return urls;
        },
        access: {
            permissions: [Permissions.ReadDiary],
            miproModules: [MiProModules.Diary]
        }
    },
];
